/**
 * Text for the shared components.
 *
 * Components in this package own their default wording under the `ui.*`
 * namespace instead of taking every label as a prop. Apps layer it into their
 * own message bundles with `mergeUiMessages` before calling `createI18n`.
 */
import { en } from './en';
import { fr } from './fr';

export { en, fr };

/** The `ui.*` defaults, keyed by language. */
export const uiMessages = { en, fr };

/** A message bundle: nested objects of strings, as vue-i18n takes them. */
type Messages = Record<string, unknown>;

/**
 * Merges `source` into `target`, with `target`'s own values winning. Only
 * plain objects are walked; anything else in `target` is left alone.
 */
function mergeDefaults(target: Messages, source: Messages): Messages {
  const merged: Messages = { ...source, ...target };
  for (const key of Object.keys(source)) {
    const from = source[key];
    const onto = target[key];
    if (isPlainObject(from) && isPlainObject(onto)) {
      merged[key] = mergeDefaults(onto, from);
    }
  }
  return merged;
}

function isPlainObject(value: unknown): value is Messages {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Layers the `ui.*` defaults underneath an app's own messages, so the app can
 * override any shared string just by defining the same key itself.
 *
 * Which defaults a locale gets is decided by its language subtag: `fr-CA` gets
 * the French text, everything else gets the English.
 *
 * Locales with more than two subtags — Connect's white-label `en-US-rmi` and
 * `fr-CA-rmi` — are deliberately left untouched. vue-i18n resolves a missing
 * key by walking the locale up one subtag at a time (`en-US-rmi` → `en-US` →
 * `en`), so those locales already inherit `ui.*` from their base locale.
 * Writing the defaults into them as well would shadow the base bundle, and a
 * later override in `en-US` would then silently not reach RMI users. A
 * genuinely RMI-specific wording change still belongs in the `-rmi` bundle.
 *
 * Verified on vue-i18n 9.14.5 and 10.0.8, for `i18n.global.t`, for `t()` from
 * `useI18n()` (what these components use), and for the injected `$t`.
 *
 * @example
 * // apps/console/src/main.ts
 * const i18n = createI18n({ locale: 'en', messages: mergeUiMessages(i18nData) });
 */
export function mergeUiMessages<T extends Messages>(appMessages: T): T {
  const result: Messages = { ...appMessages };

  for (const locale of Object.keys(appMessages)) {
    const subtags = locale.split('-');
    if (subtags.length > 2) continue;

    const defaults = subtags[0] === 'fr' ? uiMessages.fr : uiMessages.en;
    const appLocale = appMessages[locale];
    result[locale] = isPlainObject(appLocale)
      ? mergeDefaults(appLocale, defaults as unknown as Messages)
      : defaults;
  }

  return result as T;
}
